<script setup lang="ts">
    import { computed, ref } from 'vue';
    import { useRouter } from 'vue-router';
    import { __ } from '@/js/stores/expressions';
    import SettingsPageLayout from '@/js/components/settings/SettingsPageLayout.vue';
    import EchoButton from '@/js/components/EchoButton.vue';
    import LoadingDots from '@/js/components/LoadingDots.vue';
    import SortArrows from '@/js/back-office/components/SortArrows.vue';
    import { useQueryGetIntegrationOverview } from '@/js/services/api/backOffice/backOfficeQueries';
    import type { IntegrationSyncOverviewRow } from '@/js/services/api/backOffice/types';
    import { GENESYS_SYSTEM_KEY } from '@/js/consts/integration-systems';

    const router = useRouter();

    const { data: overviewData, isLoading: isLoadingOverview } =
        useQueryGetIntegrationOverview(GENESYS_SYSTEM_KEY);

    const dataTypeLabels = computed<Record<string, string>>(() => ({
        Agents: __('back-office-genesys-data-type-agents'),
        CallInteractions: __('back-office-genesys-data-type-interactions'),
        EmailInteractions: __('back-office-genesys-data-type-email-interactions'),
    }));

    const formatLastSynced = (date: string | null): string =>
        date ? new Date(date).toLocaleString() : __('back-office-genesys-overview-never-synced');

    const editTenant = (tenantId: string) => {
        router.push({ path: '/back-office/genesys-config', query: { tenantId } });
    };

    type SortColumn = 'customer' | 'dataType' | 'active' | 'lastSynced';
    const sortColumn = ref<SortColumn | null>(null);
    const sortDirection = ref<'asc' | 'desc'>('asc');

    function setSort(col: SortColumn, dir: 'asc' | 'desc') {
        sortColumn.value = col;
        sortDirection.value = dir;
    }

    const sortKeyGetters: Record<SortColumn, (row: IntegrationSyncOverviewRow) => string | number> = {
        customer: (row) => row.tenantName,
        dataType: (row) => dataTypeLabels.value[row.dataType],
        active: (row) => (row.isEnabled ? 1 : 0),
        lastSynced: (row) => row.lastSyncedAt ?? '',
    };

    const sortedRows = computed(() => {
        const rows = [...(overviewData.value ?? [])];
        if (!sortColumn.value) return rows;
        const getter = sortKeyGetters[sortColumn.value];
        return rows.sort((a, b) => {
            const valA = getter(a);
            const valB = getter(b);
            const result = valA < valB ? -1 : valA > valB ? 1 : 0;
            return sortDirection.value === 'asc' ? result : -result;
        });
    });
</script>
<template>
    <SettingsPageLayout>
        <template #header>
            <h1 class="heading-lg-light">{{ __('back-office-genesys-tab-overview') }}</h1>
        </template>
        <template #content>
            <h2 class="heading-md mb-4">{{ __('back-office-genesys-overview-heading') }}</h2>
            <div class="overflow-x-auto">
                <table class="w-full border-collapse">
                    <thead>
                        <tr class="border-b border-gray-lightest">
                            <th class="py-3 pr-6 text-left text-caption-bold uppercase">
                                <span class="flex items-center gap-1">
                                    {{ __('back-office-genesys-overview-col-customer') }}
                                    <SortArrows
                                        :asc-disabled="
                                            sortColumn === 'customer' && sortDirection === 'asc'
                                        "
                                        :desc-disabled="
                                            sortColumn === 'customer' && sortDirection === 'desc'
                                        "
                                        @sort="(dir) => setSort('customer', dir)"
                                    />
                                </span>
                            </th>
                            <th class="py-3 pr-6 text-left text-caption-bold uppercase">
                                <span class="flex items-center gap-1">
                                    {{ __('back-office-genesys-overview-col-data-type') }}
                                    <SortArrows
                                        :asc-disabled="
                                            sortColumn === 'dataType' && sortDirection === 'asc'
                                        "
                                        :desc-disabled="
                                            sortColumn === 'dataType' && sortDirection === 'desc'
                                        "
                                        @sort="(dir) => setSort('dataType', dir)"
                                    />
                                </span>
                            </th>
                            <th class="py-3 pr-6 text-left text-caption-bold uppercase">
                                <span class="flex items-center gap-1">
                                    {{ __('back-office-genesys-overview-col-active') }}
                                    <SortArrows
                                        :asc-disabled="
                                            sortColumn === 'active' && sortDirection === 'asc'
                                        "
                                        :desc-disabled="
                                            sortColumn === 'active' && sortDirection === 'desc'
                                        "
                                        @sort="(dir) => setSort('active', dir)"
                                    />
                                </span>
                            </th>
                            <th class="py-3 pr-6 text-left text-caption-bold uppercase">
                                <span class="flex items-center gap-1">
                                    {{ __('back-office-genesys-overview-col-last-synced') }}
                                    <SortArrows
                                        :asc-disabled="
                                            sortColumn === 'lastSynced' && sortDirection === 'asc'
                                        "
                                        :desc-disabled="
                                            sortColumn === 'lastSynced' && sortDirection === 'desc'
                                        "
                                        @sort="(dir) => setSort('lastSynced', dir)"
                                    />
                                </span>
                            </th>
                            <th class="py-3 text-left text-caption-bold uppercase" />
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="isLoadingOverview">
                            <td colspan="5" class="py-12 text-center"><LoadingDots /></td>
                        </tr>
                        <template v-else>
                            <tr v-if="!overviewData?.length">
                                <td colspan="5" class="py-6 text-body text-gray-medium text-center">
                                    {{ __('back-office-genesys-overview-empty') }}
                                </td>
                            </tr>
                            <tr
                                v-for="row in sortedRows"
                                :key="`${row.tenantId}-${row.dataType}`"
                                class="border-b border-gray-lightest"
                            >
                                <td class="py-3 pr-6 text-left text-body">{{ row.tenantName }}</td>
                                <td class="py-3 pr-6 text-left text-body">
                                    {{ dataTypeLabels[row.dataType] }}
                                </td>
                                <td
                                    class="py-3 pr-6 text-left text-body"
                                    :class="row.isEnabled ? 'text-success' : 'text-error'"
                                >
                                    {{ row.isEnabled ? __('active') : __('inactive') }}
                                </td>
                                <td class="py-3 pr-6 text-left text-body">
                                    {{ formatLastSynced(row.lastSyncedAt) }}
                                </td>
                                <td class="py-3 text-left text-body">
                                    <EchoButton
                                        type="button"
                                        variant="secondary"
                                        @click="editTenant(row.tenantId)"
                                    >
                                        {{ __('edit') }}
                                    </EchoButton>
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </div>
        </template>
    </SettingsPageLayout>
</template>
